import { formatLocalDate } from '../../utils/localDateTime';
import React from 'react';
import type { EpAdvanceReport } from '@types';

// ── Helper badges ──────────────────────────────────────────────────────────────
const ReadinessBadge: React.FC<{ value?: string }> = ({ value }) => {
    const v = (value || '').toLowerCase();
    const cls = v === 'green' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
        : v === 'amber' ? 'bg-amber-100 text-amber-800 border-amber-300'
        : v === 'red' ? 'bg-red-100 text-red-700 border-red-300'
        : 'bg-slate-100 text-slate-600 border-slate-300';
    const dot = v === 'green' ? 'bg-emerald-500' : v === 'amber' ? 'bg-amber-500' : v === 'red' ? 'bg-red-500' : 'bg-slate-400';
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-bold uppercase tracking-wide ${cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {value || 'Unknown'}
        </span>
    );
};

const PriorityBadge: React.FC<{ value?: string }> = ({ value }) => {
    const v = (value || '').toUpperCase();
    const cls = v === 'CRITICAL' ? 'bg-red-100 text-red-700 border-red-300'
        : v === 'HIGH' ? 'bg-orange-100 text-orange-700 border-orange-300'
        : 'bg-blue-100 text-blue-700 border-blue-300';
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${cls}`}>
            {value || 'MEDIUM'}
        </span>
    );
};

const RiskBadge: React.FC<{ value?: string }> = ({ value }) => {
    const v = (value || '').toLowerCase();
    const cls = v === 'high' || v === 'critical' ? 'text-red-600 font-semibold'
        : v === 'medium' || v === 'amber' ? 'text-amber-600 font-semibold'
        : 'text-emerald-600 font-semibold';
    return <span className={cls}>{value || '—'}</span>;
};

const InfoRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-3 py-2 border-b border-slate-100 last:border-0">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide min-w-[160px]">{label}</span>
        <span className="text-sm text-slate-800">{value || '—'}</span>
    </div>
);

const ReportSection: React.FC<{ title: string; badge?: React.ReactNode; children: React.ReactNode }> = ({ title, badge, children }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-widest">{title}</h3>
            {badge}
        </div>
        <div className="p-5">{children}</div>
    </div>
);

// ── Main Display Component ─────────────────────────────────────────────────────
interface EpAdvanceSurveyDisplayProps {
    report: EpAdvanceReport;
}

const EpAdvanceSurveyDisplay: React.FC<EpAdvanceSurveyDisplayProps> = ({ report: r }) => {
    return (
        <div className="space-y-5 print:p-0">
            {/* Classification Banner */}
            <div className="flex items-center justify-between bg-slate-900 text-white rounded-xl px-5 py-3 print:rounded-none">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold tracking-widest text-amber-400 uppercase border border-amber-500 px-2 py-0.5 rounded">
                        {r.classificationLevel || 'CONFIDENTIAL'}
                    </span>
                    <span className="text-sm font-bold tracking-wide">{r.operationName || 'CLASSIFIED OPERATION'}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span>D-Day: <span className="text-white font-semibold">{formatLocalDate(r.visitDate, 'TBD')}</span></span>
                    <span>Prepared: <span className="text-white font-semibold">{formatLocalDate(r.preparedDate, '—')}</span></span>
                    <ReadinessBadge value={r.overallReadiness} />
                </div>
            </div>

            {/* Principal & Threat Summary Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Principal', value: r.principalName || '—' },
                    { label: 'Role', value: r.principalTitle || '—' },
                    { label: 'Threat Level', value: r.threatLevel || '—' },
                    { label: 'Operation', value: r.operationName || '—' },
                ].map(c => (
                    <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{c.label}</p>
                        <p className="text-sm font-bold text-slate-800 truncate">{c.value}</p>
                    </div>
                ))}
            </div>

            {/* Readiness Summary */}
            {r.readinessSummary && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 leading-relaxed">
                    <span className="font-bold text-blue-700 uppercase text-[10px] tracking-widest block mb-1">Advance Readiness Assessment</span>
                    {r.readinessSummary}
                </div>
            )}

            {/* Section 1 — Mission Overview */}
            <ReportSection title="1 — Mission Overview">
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.missionOverview?.operationSummary}</p>
                <InfoRow label="Visit Purpose" value={r.missionOverview?.visitPurpose} />
                <InfoRow label="Site Suitability" value={r.missionOverview?.siteOverview} />
            </ReportSection>

            {/* Section 2 — Site Assessment */}
            <ReportSection title="2 — Site Assessment" badge={<ReadinessBadge value={r.siteAssessment?.overallPosture} />}>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.siteAssessment?.summary}</p>
                <InfoRow label="CCTV Coverage" value={r.siteAssessment?.cctvAssessment} />
                <InfoRow label="Access Control" value={r.siteAssessment?.accessControlAssessment} />
                <InfoRow label="Perimeter" value={r.siteAssessment?.perimeterAssessment} />
                {(r.siteAssessment?.identifiedVulnerabilities?.length > 0) && (
                    <div className="mt-3">
                        <p className="text-[10px] font-bold text-red-600 uppercase tracking-widest mb-1">Identified Vulnerabilities</p>
                        <ul className="space-y-1">{r.siteAssessment.identifiedVulnerabilities.map((v: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-500 mt-0.5">⚠</span>{v}</li>
                        ))}</ul>
                    </div>
                )}
                {(r.siteAssessment?.recommendedMitigations?.length > 0) && (
                    <div className="mt-3">
                        <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest mb-1">Recommended Mitigations</p>
                        <ul className="space-y-1">{r.siteAssessment.recommendedMitigations.map((m: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-emerald-500 mt-0.5">✓</span>{m}</li>
                        ))}</ul>
                    </div>
                )}
            </ReportSection>

            {/* Section 3 — Entry / Egress */}
            <ReportSection title="3 — Entry / Egress Plan">
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.entryEgressPlan?.summary}</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    {[
                        { title: 'Primary Route', data: r.entryEgressPlan?.primaryRoute },
                        { title: 'Secondary Route', data: r.entryEgressPlan?.secondaryRoute },
                        { title: 'Contingency Route', data: r.entryEgressPlan?.contingencyRoute },
                    ].map(route => route.data && (
                        <div key={route.title} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{route.title}</p>
                            <p className="text-sm text-slate-700 mb-1">{route.data.description}</p>
                            <p className="text-xs text-slate-500">Risk: <RiskBadge value={route.data.riskRating} /></p>
                            {route.data.chokePoints && <p className="text-xs text-amber-700 mt-1">⚠ {route.data.chokePoints}</p>}
                        </div>
                    ))}
                </div>
                {r.entryEgressPlan?.paceJourneys && <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{r.entryEgressPlan.paceJourneys}</p>}
                {r.entryEgressPlan?.emergencyEvacuation && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold text-red-700 uppercase tracking-widest mb-2">Emergency Evacuation Route (EER)</p>
                        <InfoRow label="EER Route" value={r.entryEgressPlan.emergencyEvacuation.eerRoute} />
                        <InfoRow label="Rally Point" value={r.entryEgressPlan.emergencyEvacuation.rallyPoint} />
                        <InfoRow label="Vehicle Staging" value={r.entryEgressPlan.emergencyEvacuation.vehicleStaging} />
                        <InfoRow label="Helicopter LZ" value={r.entryEgressPlan.emergencyEvacuation.helicopterLZ} />
                    </div>
                )}
            </ReportSection>

            {/* Section 4 — Safe Room */}
            <ReportSection title="4 — Safe Room & Medical Protocols" badge={<span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">CRITICAL</span>}>
                <InfoRow label="Safe Room Location" value={r.safeRoomProtocol?.location} />
                <InfoRow label="Communications" value={r.safeRoomProtocol?.communications} />
                <InfoRow label="Secure Holding Area" value={r.safeRoomProtocol?.holdingArea} />
                {r.safeRoomProtocol?.medicalResources && (
                    <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest mb-2">Medical Resources</p>
                        <InfoRow label="IFAK / TCCC Kit" value={r.safeRoomProtocol.medicalResources.kitLocation} />
                        <InfoRow label="AED Location" value={r.safeRoomProtocol.medicalResources.aedLocation} />
                        <InfoRow label="Golden Hour Assessment" value={r.safeRoomProtocol.medicalResources.goldenHourAssessment} />
                    </div>
                )}
            </ReportSection>

            {/* Section 5 — Counter-Surveillance */}
            <ReportSection title="5 — Counter-Surveillance Assessment">
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.counterSurveillance?.summary}</p>
                <InfoRow label="SDR Route" value={r.counterSurveillance?.sdrRoute} />
                <InfoRow label="Static Surveillance Risk" value={r.counterSurveillance?.staticRisk} />
                <InfoRow label="Mobile Surveillance Risk" value={r.counterSurveillance?.mobileRisk} />
                <InfoRow label="Hostile Recon Indicators" value={r.counterSurveillance?.hostileReconIndicators} />
                {(r.counterSurveillance?.recommendations?.length > 0) && (
                    <div className="mt-3">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">CS Recommendations</p>
                        <ul className="space-y-1">{r.counterSurveillance.recommendations.map((rec: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-blue-500 mt-0.5">→</span>{rec}</li>
                        ))}</ul>
                    </div>
                )}
            </ReportSection>

            {/* Section 6 — Emergency Services */}
            <ReportSection title="6 — Emergency Services Coordination" badge={<ReadinessBadge value={r.emergencyServicesCoordination?.goldenHourStatus} />}>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.emergencyServicesCoordination?.coordinationAssessment}</p>
                <InfoRow label="Nearest Hospital" value={`${r.emergencyServicesCoordination?.hospital?.name || '—'} (${r.emergencyServicesCoordination?.hospital?.distance || '?'})`} />
                <InfoRow label="Trauma Assessment" value={r.emergencyServicesCoordination?.hospital?.traumaLevel} />
                <InfoRow label="Level I Trauma Centre" value={r.emergencyServicesCoordination?.traumaCenter} />
                <InfoRow label="Medevac LZ" value={r.emergencyServicesCoordination?.medevacLZ} />
                <InfoRow label="Police Contact" value={r.emergencyServicesCoordination?.policeContact} />
                <InfoRow label="Fire Service" value={r.emergencyServicesCoordination?.fireContact} />
            </ReportSection>

            {/* Section 7 — Communications Plan */}
            <ReportSection title="7 — Communications Plan (PACE)">
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.communicationsPlan?.paceAssessment}</p>
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        { label: 'Command Channel', value: r.communicationsPlan?.commandChannel },
                        { label: 'Tactical Channel', value: r.communicationsPlan?.tacticalChannel },
                        { label: 'Emergency Channel', value: r.communicationsPlan?.emergencyChannel },
                    ].map(ch => (
                        <div key={ch.label} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{ch.label}</p>
                            <p className="text-sm font-semibold text-slate-800">{ch.value || '—'}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    <div className={`rounded-lg p-3 border ${!r.communicationsPlan?.duressWord || r.communicationsPlan.duressWord.includes('NOT SET') ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-200'}`}>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1 text-red-700">Duress Word / Code</p>
                        <p className="text-sm font-bold text-slate-800">{r.communicationsPlan?.duressWord || '⚠ NOT CONFIGURED'}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Lost-Comms Procedure</p>
                        <p className="text-sm text-slate-700">{r.communicationsPlan?.lostCommsProcedure || '—'}</p>
                    </div>
                </div>
                {r.communicationsPlan?.vulnerabilities && (
                    <p className="mt-2 text-xs text-amber-700">⚠ {r.communicationsPlan.vulnerabilities}</p>
                )}
            </ReportSection>

            {/* Section 8 — TSCM */}
            <ReportSection title="8 — TSCM Status">
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.tscmStatus?.riskAssessment}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <InfoRow label="Sweep Required" value={r.tscmStatus?.sweepRequired} />
                    <InfoRow label="TSCM Team" value={r.tscmStatus?.sweepTeam} />
                    <InfoRow label="Principal Accommodation" value={r.tscmStatus?.accommodationStatus} />
                    <InfoRow label="Command Post" value={r.tscmStatus?.commandPostStatus} />
                </div>
                {(r.tscmStatus?.pendingActions?.length > 0) && (
                    <div className="mt-3">
                        <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-1">Pending Actions</p>
                        <ul className="space-y-1">{r.tscmStatus.pendingActions.map((a: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-amber-500 mt-0.5">●</span>{a}</li>
                        ))}</ul>
                    </div>
                )}
            </ReportSection>

            {/* Section 9 — Advance Agent Recommendations */}
            {r.advanceAgentRecommendations?.length > 0 && (
                <ReportSection title="9 — Advance Agent Recommendations" badge={<span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">ACTION REQUIRED</span>}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-slate-200">
                                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2 pr-3 w-24">Priority</th>
                                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2 pr-3">Action</th>
                                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2 pr-3 w-32">Deadline</th>
                                <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest py-2 w-32">Owner</th>
                            </tr></thead>
                            <tbody>{r.advanceAgentRecommendations.map((rec: any, i: number) => (
                                <tr key={i} className="border-b border-slate-100 last:border-0">
                                    <td className="py-2 pr-3"><PriorityBadge value={rec.priority} /></td>
                                    <td className="py-2 pr-3 text-slate-700">{rec.action}</td>
                                    <td className="py-2 pr-3 text-slate-500 text-xs">{rec.deadline}</td>
                                    <td className="py-2 text-slate-500 text-xs">{rec.owner}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                </ReportSection>
            )}

            {/* Section 10 — Open Items */}
            {r.openItems?.length > 0 && (
                <ReportSection title="10 — Open Items">
                    <div className="space-y-2">{r.openItems.map((item: any, i: number) => (
                        <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                            <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${item.status === 'Outstanding' ? 'bg-red-100 text-red-700' : item.status === 'In Progress' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {item.status}
                            </span>
                            <div className="flex-1">
                                <p className="text-sm text-slate-800">{item.item}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{item.owner} · {item.deadline}</p>
                            </div>
                        </div>
                    ))}</div>
                </ReportSection>
            )}

            {/* Advance Notes */}
            {r.advanceNotes && r.advanceNotes !== 'None recorded.' && (
                <ReportSection title="Advance Agent Notes">
                    <p className="text-sm text-slate-700 leading-relaxed">{r.advanceNotes}</p>
                </ReportSection>
            )}

            {/* Footer */}
            <div className="text-center text-[10px] text-slate-400 py-4 border-t border-slate-200 print:block">
                {r.classificationLevel || 'CONFIDENTIAL — EP ADVANCE TEAM ONLY'} · Generated {formatLocalDate(r.preparedDate, r.preparedDate || '')} · IntelOptics Intelligence Platform
            </div>
        </div>
    );
};

export default EpAdvanceSurveyDisplay;
